"use client";

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { MaterialityPlanning } from "./materiality-planning";
import { MaterialityRevision } from "./materiality-revision";
import { AbcotdMateriality } from "./abcotd-materiality";
import { ComponentMateriality } from "./component-materiality";
import { DocumentationMateriality } from "./documentation-materiality";
import { Calculator, FileSignature } from "lucide-react";
import { getCompanyName } from "@/lib/textron-data-processor";
import { useMaterialityData } from "./hooks/useMaterialityData";
import { MaterialitySummary } from "./components/MaterialitySummary";
import { MaterialityTabNavigation } from "./components/MaterialityTabNavigation";
import { Button } from "../ui/button";
import PlanningSignoffDialog, { PlanningSignoffTokens } from "./PlanningSignoffDialog";
import { useAuth } from "@/contexts/AuthContext";
import { loadMiscSignoffs, getSignoffEntriesForCard, type MiscSignoffEntry } from "@/utils/misc-signoff-utils";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";

/**
 * Main MaterialitySection component that manages the materiality assessment workflow.
 * This component handles the overall state and coordination between different materiality tabs.
 */
const WORKSPACE_NAME = "Materiality";

export function MaterialitySection() {
  const [companyName, setCompanyName] = useState<string>("");
  const [ep1Answer, setEp1Answer] = useState<string | null>(null);
  const [loadingEp1, setLoadingEp1] = useState(true);
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [cardSignoffs, setCardSignoffs] = useState<MiscSignoffEntry[]>([]);

  const loadCardSignoffs = React.useCallback(async () => {
    try {
      const data = await loadMiscSignoffs();
      setCardSignoffs(getSignoffEntriesForCard(WORKSPACE_NAME, WORKSPACE_NAME, data.signoff));
    } catch {
      setCardSignoffs([]);
    }
  }, []);

  useEffect(() => {
    loadCardSignoffs();
  }, [loadCardSignoffs]);

  useEffect(() => {
    const loadUserEngagementRole = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const currentUserMember = team.find((member: any) => String(member.id) === String(user.id));
        setUserEngagementRole(currentUserMember?.role ?? null);
      } catch {
        setUserEngagementRole(null);
      }
    };
    loadUserEngagementRole();
  }, [currentEngagement, user]);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load EP-1 answer from SharePoint when Materiality section mounts
  useEffect(() => {
    const loadEp1FromSharePoint = async () => {
      if (!companyName) {
        setLoadingEp1(false);
        return;
      }

      try {
        const planningAPI = (window as any).planningAPI;
        if (!planningAPI?.loadResponses) {
          console.log("Planning API not available for EP-1 check");
          setLoadingEp1(false);
          return;
        }

        // Load Engagement Acceptance responses from SharePoint
        const sharePointResponses = await planningAPI.loadResponses({
          companyName,
          sectionKey: "engagement-acceptance"
        });

        if (sharePointResponses && sharePointResponses.success && sharePointResponses.data) {
          const responses = sharePointResponses.data.responses || sharePointResponses.data;
          
          // Check for EP-1 in the responses
          if (responses['EP-1'] && responses['EP-1'].selectedOption) {
            setEp1Answer(responses['EP-1'].selectedOption);
            console.log("EP-1 loaded from SharePoint in MaterialitySection:", responses['EP-1'].selectedOption);
          } else {
            // EP-1 not found or not answered
            setEp1Answer(null);
            console.log("EP-1 not found in SharePoint responses or not answered");
          }
        } else {
          setEp1Answer(null);
        }
      } catch (error) {
        console.error("Failed to load EP-1 from SharePoint in MaterialitySection:", error);
        setEp1Answer(null);
      } finally {
        setLoadingEp1(false);
      }
    };

    loadEp1FromSharePoint();
  }, [companyName]);

  const {
    planningData,
    abcotdData,
    componentData,
    documentationData,
    activeTab,
    lastSaved,
    crossSectionAnswers,
    handlePlanningDataChange,
    handleRevisionComplete,
    handleAbcotdDataChange,
    handleComponentDataChange,
    handleDocumentationDataChange,
    setActiveTab,
    isPlanningComplete,
  } = useMaterialityData(companyName);

  // Load revised materiality data from SharePoint
  const [revisedMaterialityData, setRevisedMaterialityData] = useState<any>(null);
  
  useEffect(() => {
    const loadRevisedMateriality = async () => {
      if (!companyName) return;

      try {
        const planningAPI = (window as any).planningAPI;
        if (!planningAPI?.loadResponses) {
          return;
        }

        const sharePointResponses = await planningAPI.loadResponses({
          companyName,
          sectionKey: "materiality"
        });

        if (sharePointResponses && sharePointResponses.success && sharePointResponses.data) {
          const responses = sharePointResponses.data.responses || sharePointResponses.data;
          
          // Check if revised materiality exists
          if (responses['revisedMateriality'] && responses['revisedMateriality'].determinedMateriality > 0) {
            setRevisedMaterialityData(responses['revisedMateriality']);
          } else {
            setRevisedMaterialityData(null);
          }
        }
      } catch (error) {
        console.error("Failed to load revised materiality data:", error);
      }
    };

    loadRevisedMateriality();
  }, [companyName, lastSaved]); // Reload when companyName or lastSaved changes

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="flex items-center gap-2 text-2xl font-bold">
            <Calculator className="h-6 w-6" />
            Materiality Assessment
          </h2>
          <p className="text-muted-foreground">
            Determine and manage materiality thresholds for audit planning and
            execution
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PlanningSignoffTokens signoffs={cardSignoffs} className="mt-0" />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSignoffDialogOpen(true)}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <FileSignature className="h-4 w-4 mr-1.5" />
            Sign off
          </Button>
          {lastSaved && (
            <Badge variant="outline" className="border-green-600 text-green-600">
              Last saved: {lastSaved.toLocaleTimeString()}
            </Badge>
          )}
        </div>
      </div>

      <PlanningSignoffDialog
        isOpen={signoffDialogOpen}
        onClose={() => setSignoffDialogOpen(false)}
        workspaceName={WORKSPACE_NAME}
        cardNames={[WORKSPACE_NAME]}
        user={user ? { user_name: (user as any).user_name ?? (user as any).name, type: (user as any).type } : null}
        userEngagementRole={userEngagementRole}
        onSignoffDone={loadCardSignoffs}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Materiality Workflow</span>
            <div className="flex items-center gap-2">
              {isPlanningComplete && (
                <Badge
                  variant="default"
                  className="bg-green-100 text-green-800"
                >
                  Planning Complete
                </Badge>
              )}
              <Badge variant="secondary">
                {activeTab === "planning" ? "Planning Stage" : "Revision Stage"}
              </Badge>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <MaterialityTabNavigation isPlanningComplete={isPlanningComplete} />

            <TabsContent value="planning" className="mt-6">
              <MaterialityPlanning
                onDataChange={handlePlanningDataChange}
                initialData={planningData}
                crossSectionAnswers={crossSectionAnswers}
                companyName={companyName}
              />
            </TabsContent>

            <TabsContent value="abcotd" className="mt-6">
              <AbcotdMateriality
                onDataChange={handleAbcotdDataChange}
                initialData={abcotdData}
                crossSectionAnswers={crossSectionAnswers}
                companyName={companyName}
              />
            </TabsContent>

            <TabsContent value="component" className="mt-6">
              <ComponentMateriality
                onDataChange={handleComponentDataChange}
                initialData={componentData}
                crossSectionAnswers={crossSectionAnswers}
                companyName={companyName}
                ep1Answer={ep1Answer}
                loadingEp1={loadingEp1}
              />
            </TabsContent>

            <TabsContent value="documentation" className="mt-6">
              <DocumentationMateriality
                onDataChange={handleDocumentationDataChange}
                initialData={documentationData}
                crossSectionAnswers={crossSectionAnswers}
                companyName={companyName}
              />
            </TabsContent>

            <TabsContent value="revision" className="mt-6">
              <MaterialityRevision
                planningData={planningData}
                onRevisionComplete={handleRevisionComplete}
                crossSectionAnswers={crossSectionAnswers}
                companyName={companyName}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Summary Card */}
      {isPlanningComplete && (
        <MaterialitySummary
          planningData={planningData}
          abcotdData={abcotdData}
          componentData={componentData}
          revisedMaterialityData={revisedMaterialityData}
        />
      )}
    </div>
  );
}
